import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const TFS = [
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "all", label: "All time" },
];

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [tf, setTf] = useState("week");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboard?timeframe=${tf}`).then((r) => {
      setRows(r.data);
    }).finally(() => setLoading(false));
  }, [tf]);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-8 px-4 md:px-8">
        <div className="flex items-center gap-3 mb-2">
          <Trophy size={22} className="text-[#00E676]" strokeWidth={2} />
          <h1 className="font-heading font-bold text-4xl tracking-tight">Leaderboard</h1>
        </div>
        <p className="text-sm text-white/45 font-mono mb-6">Top earners with verified income proofs.</p>

        <div className="surface rounded-xl p-2 mb-5 flex items-center gap-1" data-testid="leaderboard-tabs">
          {TFS.map((t) => (
            <button
              key={t.key}
              data-testid={`tf-${t.key}`}
              onClick={() => setTf(t.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition ${
                tf === t.key ? "bg-white/10 text-white" : "text-white/45 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="surface rounded-xl h-16 shimmer" />)}</div>
        ) : rows.length === 0 ? (
          <div className="surface rounded-xl p-12 text-center">
            <Crown size={32} className="text-[#F4E8C1] mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-heading font-bold text-xl mb-1">No earnings yet.</div>
            <div className="text-sm text-white/50">Be the first to drop an income proof.</div>
          </div>
        ) : (
          <div className="surface rounded-xl overflow-hidden">
            {rows.map((r, i) => (
              <motion.div
                key={r.user.user_id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03 }}
                data-testid={`leaderboard-row-${r.user.user_id}`}
                className="flex items-center justify-between p-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors last:border-0"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`font-mono text-xl w-8 text-center font-semibold ${
                    i === 0 ? "text-[#F4E8C1]" : i === 1 ? "text-white/80" : i === 2 ? "text-[#CD7F32]" : "text-white/30"
                  }`}>
                    {i + 1}
                  </div>
                  <Link to={`/profile/${r.user.user_id}`}>
                    <img
                      src={r.user.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${r.user.user_id}`}
                      alt=""
                      className="w-10 h-10 rounded-md object-cover bg-white/5"
                    />
                  </Link>
                  <div className="min-w-0">
                    <Link to={`/profile/${r.user.user_id}`} className="font-semibold hover:text-[#00E676] transition truncate block">
                      {r.user.name}
                    </Link>
                    <div className="text-[11px] font-mono text-white/40 truncate">
                      {(r.user.niches || []).join(" · ") || "no niches"} · {r.proofs} proof{r.proofs !== 1 && "s"}
                    </div>
                  </div>
                </div>
                <div className="font-mono text-lg text-[#00E676] tracking-tight font-semibold whitespace-nowrap">
                  ${Math.round(r.total).toLocaleString()}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
